import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useLenis } from "@/lib/useLenis";
import React, { useEffect } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import BottomNav from "@/components/BottomNav";
import PWAPrompt from "@/components/PWAPrompt";
import AskPulse from "@/components/AskPulse";
import FloatingListenBar from "@/components/FloatingListenBar";
import LocalAlertBanner from "@/components/LocalAlertBanner";
import { IS_FARMERSPULSE } from "@/lib/site";
import { Toaster } from "sonner";

import Home from "@/pages/Home";
import About from "@/pages/About";
import ClimateChange from "@/pages/ClimateChange";
import NatureMuseum from "@/pages/NatureMuseum";
import Weather from "@/pages/Weather";
import WeatherAlmanac from "@/pages/WeatherAlmanac";
import CivicReports from "@/pages/CivicReports";
import Contact from "@/pages/Contact";
import Dashboard from "@/pages/Dashboard";
import FinishSignIn from "@/pages/FinishSignIn";
import Admin from "@/pages/Admin";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PhotoPulse from "@/pages/PhotoPulse";
import Pulse from "@/pages/Pulse";
import FeelThePulseStory from "@/pages/FeelThePulseStory";
import Faq from "@/pages/Faq";
import Challenges from "@/pages/Challenges";
import Manifesto from "@/pages/Manifesto";
import LivingRecord from "@/pages/LivingRecord";
import Stories from "@/pages/Stories";
import StoryDetail from "@/pages/StoryDetail";
import CreateStory from "@/pages/CreateStory";
import News from "@/pages/News";
import Articles from "@/pages/Articles";
import CmsArticlePage from "@/pages/CmsArticlePage";
import CatalogueExplore from "@/pages/catalogue/CatalogueExplore";
import CatalogueHub from "@/pages/catalogue/CatalogueHub";
import CatalogueEntity from "@/pages/catalogue/CatalogueEntity";
import IdentifyPage from "@/pages/identify/IdentifyPage";
import PlantPulseHome from "@/pages/plants/PlantPulseHome";
import SpeciesPulseHome from "@/pages/species/SpeciesPulseHome";
import SpeciesGroupPage from "@/pages/species/SpeciesGroupPage";
import SpeciesProfilePage from "@/pages/species/SpeciesProfilePage";
import SpeciesMapPage from "@/pages/species/SpeciesMapPage";
import NaturePassport from "@/pages/NaturePassport";
import { FarmersOrderSuccess, FarmersOrderCancel } from "@/pages/FarmersOrderResult";
import EventPage from "@/pages/EventPage";
import SearchPage from "@/pages/SearchPage";
import Observations from "@/pages/Observations";
import ObservationDetail from "@/pages/ObservationDetail";
import ObservationsMap from "@/pages/ObservationsMap";
import GeographicPage from "@/pages/GeographicPage";
import Notifications from "@/pages/Notifications";
import SocialPulseCallback from "@/pages/SocialPulseCallback";
import AdoptedPlaces from "@/pages/AdoptedPlaces";
import AdoptedPlaceDetail from "@/pages/AdoptedPlaceDetail";
import ResearchHub from "@/pages/ResearchHub";
import ExpertsDirectory from "@/pages/ExpertsDirectory";
import ExpertsApply from "@/pages/ExpertsApply";
import ExpertProfile from "@/pages/ExpertProfile";
import ExpertDashboard from "@/pages/ExpertDashboard";
import ResearchRequestsHub from "@/pages/ResearchRequestsHub";
import CollectionDetail from "@/pages/CollectionDetail";
import FarmersPulseRoutes from "@/farmers_pulse/routes";
import WeatherPulseRoutes from "@/weather_pulse/routes";
import SciencePulseRoutes from "@/science_pulse/routes";
import FarmersStandaloneHeader from "@/farmers_pulse/standalone/Header";
import FarmersStandaloneFooter from "@/farmers_pulse/standalone/Footer";
import OrgLanding from "@/pages/OrgLanding";
import OrgHub from "@/pages/OrgHub";
import OrgGarden from "@/pages/OrgGarden";
import GeoTree from "@/pages/GeoTree";
import YouthProfile from "@/pages/YouthProfile";
import YouthPrivacyPolicy from "@/pages/YouthPrivacyPolicy";
import StewardConsole from "@/pages/StewardConsole";
import { FarmersCropArticlesIndex, FarmersCropArticleEditor } from "@/pages/FarmersCropArticles";
import PublisherDashboard from "@/pages/PublisherDashboard";
import PublisherEditor from "@/pages/PublisherEditor";
import AdminCms from "@/pages/AdminCms";
import ThenAndNow from "@/pages/ThenAndNow";
import FounderStory from "@/pages/FounderStory";
import FounderRoadmap from "@/pages/FounderRoadmap";
import LegalHub from "@/pages/legal/LegalHub";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import EuCookieConsent from "@/pages/legal/EuCookieConsent";
import AiPolicy from "@/pages/legal/AiPolicy";
import UserContentPolicy from "@/pages/legal/UserContentPolicy";
import CommunityGuidelines from "@/pages/legal/CommunityGuidelines";
import ResearchPolicy from "@/pages/legal/ResearchPolicy";

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    // If the URL has a hash (e.g. /legal/terms#ai-features), let the target
    // page handle scroll-to-anchor. Otherwise reset to top.
    if (hash) return;
    // Fire on pathname AND on search-param change so category / topic /
    // language filter clicks also jump the reader back to the article
    // title. The `smooth` behaviour respects prefers-reduced-motion via
    // the browser.
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [pathname, search, hash]);
  return null;
}

function AppShell() {
  useLenis();
  const { pathname } = useLocation();
  // Public farm-site route (`/f/*`) renders its own theme-driven chrome
  // — hide NaturePulse Nav/Footer/BottomNav to give the farmer's site
  //   the full viewport.
  const isFarmSite = pathname.startsWith("/f/");
  // FarmersPulse mode:
  //   - Standalone build (REACT_APP_SITE=farmerspulse, deployed at
  //     farmerspulse.com) — every page is a FarmersPulse page, so we swap
  //     chrome globally.
  //   - Embedded (`/farmers/*` inside naturepulse.net) — kept for
  //     backwards compat during migration; will 301 to farmerspulse.com
  //     via the redirects below once the fork goes live.
  const isFarmersPulseRoute = pathname === "/farmers" || pathname.startsWith("/farmers/");
  const isFarmersPulse = isFarmersPulseRoute || IS_FARMERSPULSE;
  const hideMainChrome = isFarmSite || isFarmersPulse;

  // Post-fork migration hook: set REACT_APP_FARMERSPULSE_REDIRECT=1 on the
  // NaturePulse deploy once farmerspulse.com is live. Any `/farmers/*`
  // visit will be sent to `farmerspulse.com/*` with the same path.
  const shouldRedirectToFarmersPulse =
    !IS_FARMERSPULSE &&
    isFarmersPulseRoute &&
    process.env.REACT_APP_FARMERSPULSE_REDIRECT === "1";

  React.useEffect(() => {
    if (shouldRedirectToFarmersPulse) {
      const base = (process.env.REACT_APP_FARMERSPULSE_URL || "https://farmerspulse.com").replace(/\/$/, "");
      const rest = pathname.replace(/^\/farmers/, "") || "/";
      window.location.replace(`${base}${rest}${window.location.search}${window.location.hash}`);
    }
  }, [pathname, shouldRedirectToFarmersPulse]);
  return (
    <div
      className="min-h-screen text-[color:var(--np-text)]"
      style={isFarmersPulse ? {
        // Farmers Pulse forest-green + cream palette when on /farmers/*.
        // These CSS variables cascade into FarmersNav, module cards, hero,
        // pricing page and every other FP surface.
        "--np-brand": "#2D8659",
        "--np-bg":    "#F8F4E9",
        "--np-live":  "#0F3D2A",
        "--np-meta":  "#3E4A3F",
      } : undefined}
    >
      {!hideMainChrome && <Nav />}
      {isFarmersPulse && (
        <FarmersStandaloneHeader basePath={IS_FARMERSPULSE ? "" : "/farmers"} />
      )}
      <ScrollToTop />
      <main>
        {IS_FARMERSPULSE ? (
          // FarmersPulse fork — only farmer routes at root URLs.
          <Routes>
            {FarmersPulseRoutes({ standalone: true })}
            {/* Shared surfaces: CMS admin + auth callback + YouthID landings
                stay available so editors can log in from farmerspulse.com. */}
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/admin/cms" element={<ProtectedRoute><AdminCms /></ProtectedRoute>} />
            <Route path="/admin/farmers/crops/articles" element={<ProtectedRoute><FarmersCropArticlesIndex /></ProtectedRoute>} />
            <Route path="/admin/farmers/crops/:slug/article" element={<ProtectedRoute><FarmersCropArticleEditor /></ProtectedRoute>} />
            <Route path="/finish-signin" element={<FinishSignIn />} />
            <Route path="/y/:code" element={<YouthProfile />} />
            <Route path="/steward/:code" element={<ProtectedRoute><StewardConsole /></ProtectedRoute>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/legal" element={<LegalHub />} />
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/cookies" element={<CookiePolicy />} />
            <Route path="/legal/cookies-eu" element={<EuCookieConsent />} />
            <Route path="/legal/ai" element={<AiPolicy />} />
            <Route path="/legal/user-content" element={<UserContentPolicy />} />
            <Route path="/legal/community" element={<CommunityGuidelines />} />
            <Route path="/legal/research" element={<ResearchPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/founder" element={<FounderStory />} />
            <Route path="/about/roadmap" element={<FounderRoadmap />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/founder" element={<FounderStory />} />
          <Route path="/about/roadmap" element={<FounderRoadmap />} />
          <Route path="/climate-change" element={<ClimateChange />} />
          <Route path="/nature-museum" element={<NatureMuseum />} />
          <Route path="/weather" element={<Weather />} />
          <Route path="/weather-almanac" element={<WeatherAlmanac />} />
          <Route path="/civic-reports" element={<CivicReports />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/finish-signin" element={<FinishSignIn />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/admin/cms" element={<ProtectedRoute><AdminCms /></ProtectedRoute>} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/legal" element={<LegalHub />} />
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/cookies" element={<CookiePolicy />} />
          <Route path="/legal/cookies-eu" element={<EuCookieConsent />} />
          <Route path="/legal/ai" element={<AiPolicy />} />
          <Route path="/legal/user-content" element={<UserContentPolicy />} />
          <Route path="/legal/community" element={<CommunityGuidelines />} />
          <Route path="/legal/research" element={<ResearchPolicy />} />
          <Route path="/photopulse" element={<PhotoPulse />} />
          <Route path="/pulse" element={<Pulse />} />
          <Route path="/pulse/story" element={<FeelThePulseStory />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/challenges" element={<Challenges />} />
          <Route path="/manifesto" element={<Manifesto />} />
          <Route path="/article/living-diary" element={<Manifesto />} />
          <Route path="/article/living-record" element={<LivingRecord />} />
          <Route path="/articles/living-record" element={<LivingRecord />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/read/:pageType/:slug" element={<CmsArticlePage />} />
          <Route path="/explore" element={<CatalogueExplore />} />
          <Route path="/explore/:hub" element={<CatalogueHub />} />
          <Route path="/explore/:hub/:slug" element={<CatalogueEntity />} />
          <Route path="/identify" element={<IdentifyPage />} />
          <Route path="/identify/:identId" element={<IdentifyPage />} />
          <Route path="/plants" element={<PlantPulseHome />} />
          <Route path="/speciespulse" element={<SpeciesPulseHome />} />
          <Route path="/speciespulse/group/:group" element={<SpeciesGroupPage />} />
          <Route path="/speciespulse/species/:slug" element={<SpeciesProfilePage />} />
          <Route path="/speciespulse/map" element={<SpeciesMapPage />} />
          <Route path="/passport" element={<NaturePassport />} />
          <Route path="/farmers/order/success" element={<FarmersOrderSuccess />} />
          <Route path="/farmers/order/cancel" element={<FarmersOrderCancel />} />
          <Route path="/events/:eventId" element={<EventPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/observations" element={<Observations />} />
          <Route path="/observations/then-and-now" element={<ThenAndNow />} />
          <Route path="/observations/:id" element={<ObservationDetail />} />
          <Route path="/adopt-a-place" element={<AdoptedPlaces />} />
          <Route path="/adopt-a-place/:id" element={<AdoptedPlaceDetail />} />
          <Route path="/research" element={<ResearchHub />} />
          <Route path="/experts" element={<ExpertsDirectory />} />
          <Route path="/experts/apply" element={<ExpertsApply />} />
          <Route path="/experts/research-requests" element={<ResearchRequestsHub />} />
          <Route path="/collections/:id" element={<CollectionDetail />} />
          <Route path="/org/:slug" element={<OrgLanding />} />
          <Route path="/org/:slug/hub" element={<OrgHub />} />
          <Route path="/org/:slug/garden/:garden_id" element={<OrgGarden />} />
          <Route path="/geo" element={<GeoTree context="naturepulse" basePath="/geo" brandLabel="NaturePulse" brandAccent="#B85F2A" />} />
          {/* Farmers Pulse — mounted from the docket. See /app/frontend/src/farmers_pulse/README.md */}
          {FarmersPulseRoutes()}
          {/* Weather Pulse — mounted from the docket. See /app/frontend/src/weather_pulse/ */}
          {WeatherPulseRoutes()}
          {/* Science Pulse — mounted from the docket. See /app/frontend/src/science_pulse/ */}
          {SciencePulseRoutes()}
          <Route path="/expert/:slug" element={<ExpertProfile />} />
          <Route path="/dashboard/expert" element={<ProtectedRoute><ExpertDashboard /></ProtectedRoute>} />
          <Route path="/publisher" element={<ProtectedRoute><PublisherDashboard /></ProtectedRoute>} />
          <Route path="/publisher/new" element={<ProtectedRoute><PublisherEditor /></ProtectedRoute>} />
          <Route path="/publisher/edit/:id" element={<ProtectedRoute><PublisherEditor /></ProtectedRoute>} />
          <Route path="/map" element={<ObservationsMap />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/stories" element={<Stories />} />
          <Route path="/stories/new" element={<ProtectedRoute><CreateStory /></ProtectedRoute>} />
          <Route path="/stories/:id" element={<StoryDetail />} />
          <Route path="/news" element={<News />} />
          <Route path="/g/:country" element={<GeographicPage />} />
          <Route path="/g/:country/:state" element={<GeographicPage />} />
          <Route path="/g/:country/:state/:city" element={<GeographicPage />} />
          <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
          <Route path="/auth/callback" element={<SocialPulseCallback />} />
          <Route path="/y/:code" element={<YouthProfile />} />
          <Route path="/steward/:code" element={<ProtectedRoute><StewardConsole /></ProtectedRoute>} />
          <Route path="/about/youth-privacy" element={<YouthPrivacyPolicy />} />
          <Route path="/policies/student-privacy" element={<YouthPrivacyPolicy />} />
          <Route path="/policies/dpdp-2023" element={<YouthPrivacyPolicy />} />
          <Route path="/admin/farmers/crops/articles" element={<ProtectedRoute><FarmersCropArticlesIndex /></ProtectedRoute>} />
          <Route path="/admin/farmers/crops/:slug/article" element={<ProtectedRoute><FarmersCropArticleEditor /></ProtectedRoute>} />
          {/* Legacy redirects */}
          <Route path="/global-warming" element={<ClimateChange />} />
          <Route path="/nature" element={<NatureMuseum />} />
          <Route path="*" element={<Home />} />
        </Routes>
        )}
      </main>
      {!hideMainChrome && <Footer />}
      {isFarmersPulse && (
        <FarmersStandaloneFooter basePath={IS_FARMERSPULSE ? "" : "/farmers"} />
      )}
      {!hideMainChrome && <BottomNav />}
      <AskPulse />
      {!isFarmSite && <FloatingListenBar />}
      {!isFarmSite && <LocalAlertBanner />}
      {!isFarmSite && <PWAPrompt />}
      <Toaster theme="light" position="bottom-right" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
